// © Microsoft Corporation. All rights reserved.

import { Comment, Content, User } from "@msnews/social-data-service";
import { CommentConnector, ComponentConnector, FollowInfo, PopupConnector, UsersConnector } from "../../Social.props";
import { chevron_down, chevron_up } from "../../utils/icons";

import CommentBox from "../comment-box/CommentBox";
import CommentItem from "../comment-item/CommentItem";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";

export interface ReplyListProps {
    componentConnector: ComponentConnector;
    id: string;
    parentId: string;
    user: User;
    commentItemType: string;
    replies: Array<Comment>;
    replyCount: number;
    repliesHasPrevious: boolean;
    repliesHasMore: boolean;
    commentConnector: CommentConnector;
    managedClasses?: SocialClassNameContract;
    contentDisabled: boolean;
    me: User;
    usersConnector: UsersConnector;
    popupConnector: PopupConnector;
    users:  { [id: string]: FollowInfo;}; // dictionary definening
    t: SocialLocalizedStrings;
    hideReplyBox: () => void;
    showReplyBox: boolean;
    market: string;
    content: Content;
    mainTelemetryObject: TelemetryObjectBase;
    theme: string;
    responsiveClass: string;
    specificCommentId?: string;
}

export default class ReplyList extends React.Component<ReplyListProps> {

    constructor(props) {
        super(props);

        this.submitCommentAndUpdateState = this.submitCommentAndUpdateState.bind(this);
    }

    showReplies(e) {
        const top = 10;
        const skip = this.props.replies.filter($i => !$i.addedOffline).length || 0;
        const showMoreReplies = this.props.commentConnector.showMoreReplies;

        if (!showMoreReplies) { return console.warn("Props missing 'commentConnector.showMoreReplies'"); }
        showMoreReplies(this.props.content.id, this.props.id, top, skip, "Time asc");
    }

    async submitCommentAndUpdateState(value) {
        const rootParentCommentId = this.props.parentId || this.props.id;
        const submitComment = this.props.commentConnector.submitComment;

        if (!submitComment) { return console.warn("Missing props 'commentConnector.submitComment'"); }
        const comment = await submitComment(this.props.content, value, rootParentCommentId, this.props.parentId ? this.props.id : undefined);

        if (comment && !comment.errorCode) {
            this.props.hideReplyBox();
        }

        return comment;
    }

    render() {
        const { componentConnector, responsiveClass, contentDisabled, me, t, managedClasses, commentConnector, replies, replyCount, repliesHasPrevious, repliesHasMore, commentItemType: parentType, user: parentUser, usersConnector, popupConnector, users, market, content, mainTelemetryObject, theme, id, specificCommentId } = this.props;
        const moreReplyCount = replyCount - (replies ? replies.filter($i => !$i.addedOffline).length : 0);
        const submitCommentAndUpdateState = this.submitCommentAndUpdateState.bind(this);
        const broadcastAsTyping = this.props.commentConnector.broadcastAsTyping;
        const commentItemType="Reply";
        const commentBoxType="Reply";
        const specificCommentMode = !!specificCommentId;
        return (
            <div className={managedClasses.replyListContainer}>
                {this.props.showReplyBox &&
                    <CommentBox {...{ componentConnector, isFirstCommenter: false, commentBoxType, me, t, managedClasses, submitCommentAndUpdateState, usersConnector, popupConnector, users, market, parentType, parentUser, broadcastAsTyping, content, mainTelemetryObject, theme, commentId: id }} />
                }

                {specificCommentMode && replies && repliesHasPrevious &&
                    <i className="cwt-link cwt-icon cwt-show-replies-btn cwt-specific-comment-mode" role="button" aria-pressed="false" aria-live="assertive"
                        onClick={() => commentConnector.loadPreviousComments(content.id, replies[0].id, id)}
                    >
                        {chevron_up}
                        <span className="cwt-icon-label">{t.SpecificCommentModeSeePreviousRepliesTxt}</span>
                    </i>
                }
                <ul className="cwt-list">
                    {replies && replies.map((item, index) =>
                        <CommentItem key={`ReplyItem-${index}`} {...{
                            componentConnector,
                            commentItemType,
                            item,
                            contentDisabled,
                            me,
                            t,
                            managedClasses,
                            sortType: "Time asc",
                            commentConnector,
                            usersConnector,
                            popupConnector,
                            users,
                            market,
                            content,
                            mainTelemetryObject,
                            theme,
                            responsiveClass,
                            specificCommentId
                        }} />
                    )}
                </ul>
                {specificCommentMode && replies && repliesHasMore &&
                    <i className="cwt-link cwt-icon cwt-show-replies-btn cwt-specific-comment-mode" role="button" aria-pressed="false" aria-live="assertive"
                        onClick={() => commentConnector.loadMoreComments(content.id, replies[replies.length-1].id, id)}
                    >
                        {chevron_down}
                        <span className="cwt-icon-label">{t.SpecificCommentModeSeeMoreRepliesTxt}</span>
                    </i>
                }
                {((specificCommentMode && replies.length === 0 && moreReplyCount > 0) || (!specificCommentMode && moreReplyCount > 0)) &&
                    <i className="cwt-link cwt-icon cwt-show-replies-btn" role="button" aria-pressed="false" aria-live="assertive" onClick={(e) => this.showReplies(e)}>
                        {chevron_down}
                        <span className="cwt-icon-label">
                            { moreReplyCount < 2 ?
                                t.SeeMoreRepliesTxt.replace("{#}", `${moreReplyCount}`) :
                                t.SeeMoreRepliesPluralTxt.replace("{#}", `${moreReplyCount}`)
                            }
                        </span>
                    </i>
                }
            </div>
        );
    }
}